import { ReactNode, useEffect, useRef } from "react";
import {
  createConfig,
  fallback,
  http,
  useAccount,
  useAccountEffect,
  WagmiProvider,
} from "wagmi";
import {
  connectorsForWallets,
  lightTheme,
  RainbowKitProvider,
} from "@rainbow-me/rainbowkit";
import {
  arbitrum,
  avalanche,
  base,
  linea,
  blast,
  Chain,
  mainnet,
  manta,
  mantle,
  mode,
  optimism,
  scroll,
  zkSync,
  xLayer,
  zircuit,
  bsc,
  unichain,
  tac,
  sonic,
  plasma,
  sepolia,
  ink,
} from "viem/chains";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  okxWallet,
  coinbaseWallet,
  metaMaskWallet,
  rabbyWallet,
  walletConnectWallet,
  bitgetWallet,
  imTokenWallet,
  ledgerWallet,
  safepalWallet,
  tokenPocketWallet,
  trustWallet,
  binanceWallet,
} from "@rainbow-me/rainbowkit/wallets";
import SDKProvider from "./SDKProvider";
import TermsAndCondition from "@components/WalletConnectModal/components/TermsAndCondition";
import { trackEvent } from "utils";
import { CONNECT_WALLET, DISCONNECT_WALLET } from "@constants/analytics";
import { useSentimint } from "@kelp-dao/sentimint-widget-sdk";
import { stable } from "@config/custom-chains";
import { useDispatch } from "react-redux";
import { updateUserData } from "@store/index";

interface WagmiProviersProps {
  children: ReactNode;
}

const ALCHEMY_ETHEREUM_KEY = "iuphmpG3ftqu_yNyvpDsFtukkzZbyqDK";
const ALCHEMY_ARBITRUM_KEY = "sJHV-fI60BWPJvFtqT-Dbnazdz6cKHTa";
const ALCHEMY_INK_KEY = "iuphmpG3ftqu_yNyvpDsFtukkzZbyqDK";
const WC_PROJECT_ID = process.env.NEXT_PUBLIC_WC_PROJECT_ID as string;

const queryClient = new QueryClient();

const connectors = connectorsForWallets(
  [
    {
      groupName: "Recommended",
      wallets: [
        metaMaskWallet,
        binanceWallet,
        coinbaseWallet,
        walletConnectWallet,
        okxWallet,
        trustWallet,
        rabbyWallet,
        safepalWallet,
        tokenPocketWallet,
        bitgetWallet,
        imTokenWallet,
        ledgerWallet,
      ],
    },
  ],
  {
    appName: "Kelp DAO",
    projectId: WC_PROJECT_ID,
  }
);

type IconExtra = {
  iconBackground: string;
  iconUrl: string;
};

type ChainWithIcons = Chain & IconExtra;

export const supportedChains: [ChainWithIcons, ...ChainWithIcons[]] = [
  {
    ...mainnet,
    iconBackground: "#fff",
    iconUrl: "/assets/chains/ethereum.svg",
  },
  {
    ...arbitrum,
    iconBackground: "#000",
    iconUrl: "/assets/chains/arbitrum.svg",
  },
  {
    ...bsc,
    iconBackground: "#000",
    iconUrl: "/assets/chains/binance.svg",
  },
  {
    ...manta,
    iconBackground: "#000",
    iconUrl: "/assets/chains/manta-pacific.svg",
  },
  {
    ...mode,
    iconBackground: "#000",
    iconUrl: "/assets/chains/mode.svg",
  },
  {
    ...blast,
    iconBackground: "#000",
    iconUrl: "/assets/chains/blast.png",
  },
  {
    ...scroll,
    iconBackground: "#000",
    iconUrl: "/assets/chains/scroll.svg",
  },
  {
    ...optimism,
    iconBackground: "#000",
    iconUrl: "/assets/chains/optimism.svg",
  },
  {
    ...base,
    iconBackground: "#000",
    iconUrl: "/assets/chains/base.svg",
  },
  {
    ...linea,
    iconBackground: "#000",
    iconUrl: "/assets/chains/linea.svg",
  },
  {
    ...zkSync,
    iconBackground: "#000",
    iconUrl: "/assets/chains/zksync.svg",
  },
  {
    ...xLayer,
    iconBackground: "#000",
    iconUrl: "/assets/chains/xlayer.svg",
  },
  {
    ...zircuit,
    iconBackground: "#000",
    iconUrl: "/assets/chains/zircuit.svg",
  },
  {
    ...unichain,
    iconBackground: "#000",
    iconUrl: "/assets/platforms/unichain.png",
  },
  {
    ...tac,
    iconBackground: "#000",
    iconUrl: "/assets/chains/tac.svg",
  },
  {
    ...avalanche,
    iconBackground: "#000",
    iconUrl: "/assets/chains/avalanche.png",
  },
  {
    ...sonic,
    iconBackground: "#000",
    iconUrl: "/assets/chains/sonic.png",
  },
  {
    ...plasma,
    iconBackground: "#000",
    iconUrl: "/assets/chains/plasma.png",
  },
  {
    ...ink,
    iconBackground: "#000",
    iconUrl: "/assets/chains/ink.svg",
  },
  {
    ...mantle,
    iconBackground: "#000",
    iconUrl: "/assets/chains/mantle.svg",
  },
  {
    ...sepolia,
    iconBackground: "#fff",
    iconUrl: "/assets/chains/ethereum.svg",
  },
  {
    ...stable,
    iconBackground: "#fff",
    iconUrl: "/assets/chains/stable.webp",
  },
];

export const wagmiConfig = createConfig({
  chains: supportedChains,
  ssr: true,
  transports: {
    [mainnet.id]: fallback([
      http(`https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.ankr.com/eth"),
    ]),
    [arbitrum.id]: fallback([
      http(`https://arb-mainnet.g.alchemy.com/v2/${ALCHEMY_ARBITRUM_KEY}`),
      http(`https://arbitrum-one-rpc.publicnode.com`),
      http("https://rpc.ankr.com/arbitrum"),
    ]),
    [manta.id]: fallback([
      http("https://pacific-rpc.manta.network/http"),
      http("https://manta-pacific.drpc.org"),
    ]),
    [ink.id]: fallback([
      http(`https://ink-mainnet.g.alchemy.com/v2/${ALCHEMY_INK_KEY}`),
      http("https://rpc.ink.com"), // TODO get alchemy key
      http("https://rpc.ankr.com/ink"),
    ]),
    [mode.id]: fallback([
      http(`https://mode-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://mainnet.mode.network/"),
      http("https://1rpc.io/mode"),
    ]),
    [blast.id]: fallback([
      http(`https://blast-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.blast.io"),
      http("https://rpc.ankr.com/blast"),
    ]),
    [scroll.id]: fallback([
      http(`https://scroll-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.scroll.io"),
      http("https://1rpc.io/scroll"),
    ]),
    [optimism.id]: fallback([
      http(`https://opt-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://mainnet.optimism.io"),
      http("https://rpc.ankr.com/optimism"),
    ]),
    [base.id]: fallback([
      http(`https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://mainnet.base.org"),
      http("https://base-rpc.publicnode.com"),
    ]),
    [linea.id]: fallback([
      http(`https://linea-mainnet.g.alchemy.com/v2/${ALCHEMY_ARBITRUM_KEY}`),
      http("https://1rpc.io/linea"),
      http("https://linea-rpc.publicnode.com"),
      http("https://linea.drpc.org"),
    ]),
    [bsc.id]: fallback([
      http("https://bsc-dataseed.binance.org"),
      http("https://rpc.ankr.com/bsc"),
    ]),
    [zkSync.id]: fallback([
      http("https://1rpc.io/zksync2-era"), // TODO update
      http("https://mainnet.era.zksync.io"), // TODO update
    ]),
    [xLayer.id]: fallback([
      http("https://rpc.xlayer.tech"), // TODO update
      http("https://xlayerrpc.okx.com"), // TODO update
    ]),
    [zircuit.id]: fallback([
      http("https://zircuit1-mainnet.p2pify.com"), // TODO update
      http("https://zircuit1-mainnet.liquify.com"), // TODO update
    ]),
    [unichain.id]: fallback([
      http(`https://unichain-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://mainnet.unichain.org"),
      http("https://unichain-rpc.publicnode.com"),
      http("https://rpc.ankr.com/unichain"),
    ]),
    [tac.id]: fallback([
      http("https://rpc.ankr.com/tac"),
      http("https://rpc.tac.build"),
    ]),
    [avalanche.id]: fallback([
      http(`https://avax-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://api.avax.network/ext/bc/C/rpc"),
      http("https://rpc.ankr.com/avalanche"),
    ]),
    [sonic.id]: fallback([
      http(`https://sonic-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://sonic-rpc.publicnode.com"),
      http("https://rpc.soniclabs.com"),
      http("https://rpc.ankr.com/sonic_mainnet"),
    ]),
    [plasma.id]: fallback([
      http(`https://plasma-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.plasma.to"),
      http("https://plasma.drpc.org"),
    ]),
    [sepolia.id]: fallback([
      http(`https://eth-sepolia.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.sepolia.dev"),
      http("https://rpc.ankr.com/eth_sepolia"),
    ]),
    [stable.id]: fallback([
      http(`https://stable-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://rpc.stable.xyz"),
    ]),
    [mantle.id]: fallback([
      http(`https://mantle-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY}`),
      http("https://mantle-rpc.publicnode.com"),
      http("https://rpc.mantle.xyz"),
      http("https://1rpc.io/mantle"),
    ]),
  },
  connectors,
});

const WagmiProvier = ({ children }: WagmiProviersProps) => {
  const defaultTheme = lightTheme({
    accentColor: "#3F6870",
    fontStack: "system",
    overlayBlur: "small",
  });

  const { updateWalletAddress, variant } = useSentimint();
  const lastSyncedAddressRef = useRef<string | null>(null);
  const hasTrackedConnectionRef = useRef<boolean>(false);
  const variantRef = useRef<string | null>(null);
  const dispatch = useDispatch();
  const pendingConnectionDataRef = useRef<{
    address: string;
    connectorName: string;
    chainId: number;
    chainName?: string;
    depositWithGainVariant: string;
  } | null>(null);
  const { address, isConnected, connector, chain } = useAccount({
    config: wagmiConfig,
  });

  // Keep variant ref up to date
  useEffect(() => {
    dispatch(updateUserData({ depositWithGainVariant: variant }));
    variantRef.current = variant;
  }, [variant, dispatch]);

  // Track CONNECT_WALLET event when variant becomes available
  useEffect(() => {
    if (variant && pendingConnectionDataRef.current) {
      const data = pendingConnectionDataRef.current;
      trackEvent(CONNECT_WALLET, {
        walletAddress: data.address,
        walletName: data.connectorName,
        chainId: data.chainId,
        chainName: data.chainName,
        depositWithGainVariant: variant,
      });
      pendingConnectionDataRef.current = null;
    }
  }, [variant]);

  useAccountEffect({
    config: wagmiConfig,
    onConnect(data) {
      hasTrackedConnectionRef.current = true;
      lastSyncedAddressRef.current = data.address;
      updateWalletAddress(data.address).catch(() => undefined);

      // If variant is already available, track immediately
      if (variantRef.current) {
        trackEvent(CONNECT_WALLET, {
          walletAddress: data.address,
          walletName: data.connector.name,
          chainId: data.chainId,
          chainName: data.chain?.name,
          depositWithGainVariant: variantRef.current,
        });
      } else {
        // Otherwise, store the connection data and wait for variant
        pendingConnectionDataRef.current = {
          address: data.address,
          connectorName: data.connector.name,
          chainId: data.chainId,
          chainName: data.chain?.name,
          depositWithGainVariant: "",
        };
      }
    },
    onDisconnect() {
      hasTrackedConnectionRef.current = false;
      lastSyncedAddressRef.current = null;
      pendingConnectionDataRef.current = null;
      updateWalletAddress(null);
      trackEvent(DISCONNECT_WALLET);
    },
  });

  useEffect(() => {
    if (!isConnected || !address) {
      return;
    }

    if (lastSyncedAddressRef.current === address) {
      return;
    }

    lastSyncedAddressRef.current = address;
    updateWalletAddress(address).catch(() => undefined);

    if (!hasTrackedConnectionRef.current && connector) {
      hasTrackedConnectionRef.current = true;

      // If variant is already available, track immediately
      if (variantRef.current) {
        trackEvent(CONNECT_WALLET, {
          walletAddress: address,
          walletName: connector.name,
          chainId: chain?.id,
          chainName: chain?.name,
          depositWithGainVariant: variantRef.current,
        });
      } else {
        // Otherwise, store the connection data and wait for variant
        pendingConnectionDataRef.current = {
          address: address,
          connectorName: connector.name,
          chainId: chain?.id || 0,
          chainName: chain?.name,
          depositWithGainVariant: "",
        };
      }
    }
  }, [address, isConnected, connector, chain, updateWalletAddress]);

  return (
    <>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider
            appInfo={{
              appName: "Kelp Dao",
              disclaimer: TermsAndCondition,
            }}
            theme={{
              ...defaultTheme,
              colors: {
                ...defaultTheme.colors,
                modalBackground: "#E4F8F8",
                modalText: "#3F6870",
              },
            }}
          >
            <SDKProvider>{children}</SDKProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </>
  );
};

export default WagmiProvier;
